import { MouseEvent, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { PortalLang, normaliseLang } from './detect';
import { isPortalI18nActive, setPortalLanguage } from './index';
import { portalLanguageHref } from '../util/marketingSite';

/**
 * The header's and footer's language pill (Aperio preset): each option is a
 * real link to this very page in that language (`?lang=xx`, so a new tab or a
 * shared link lands right), and a plain click switches in place - no
 * navigation, no reload, so the booking in progress keeps its step, its
 * basket and every field typed so far (QA 2026-09-25 #26). The choice is
 * remembered for the next visit.
 */
export default function useLanguageSwitch() {
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const active = isPortalI18nActive();
  const lang: PortalLang = normaliseLang(i18n.language) || 'en';

  const hrefFor = useCallback(
    (code: string) => portalLanguageHref(
      location.pathname,
      location.search,
      code,
    ),
    [location.pathname, location.search],
  );

  const switchTo = useCallback((code: string, event?: MouseEvent) => {
    const next = normaliseLang(code);
    if (!next) return;
    if (event) {
      // Let the browser open a new tab / window as usual.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }
      event.preventDefault();
    }
    // A `?lang=` left on the URL would win over the choice on reload:
    // keep it in step (same path, same state, nothing remounts).
    const params = new URLSearchParams(location.search);
    if (params.has('lang') || params.has('locale')) {
      params.delete('locale');
      params.set('lang', next);
      navigate(
        { pathname: location.pathname, search: `?${params.toString()}` },
        { replace: true, state: location.state },
      );
    }
    setPortalLanguage(next, { persist: true });
  }, [location.pathname, location.search, location.state, navigate]);

  return {
    active, lang, hrefFor, switchTo,
  };
}
